import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { useTable } from '@tanstack/react-table';
import type { DashboardBorrowPositionsFilters as DashboardFilters } from '@/helpers/filter/table';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client/useTableSettings';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { useBorrowPositionsTableData } from '@/hooks/positions/useBorrowPositionsTableData';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { useTableFiltersHandler } from '../../hooks/useTableFiltersHandler';
import { borrowPositionsTableFeatures, columnLabelsBorrowPositions, columnsBorrowPositions } from '../config/BorrowPositionsTable.columns';
import { BORROW_POSITIONS_DEFAULT_TABLE_SETTINGS, BORROW_POSITIONS_STORAGE_KEY } from '../config/config';
import { useBorrowPositionsTableRows } from './useBorrowPositionsTableRows';
import { useDashboardBorrowPositionsFilters } from './useDashboardBorrowPositionsFilters';
export const useDashboardBorrowPositionsTable = () => {
  const $ = _c(34);
  const {
    address: accountAddress
  } = useViewedAccountAddress();
  let t0;
  if ($[0] !== accountAddress) {
    t0 = {
      pageKey: PageKey.Dashboard,
      tableSettingsObjectKey: BORROW_POSITIONS_STORAGE_KEY,
      defaultTableSettings: BORROW_POSITIONS_DEFAULT_TABLE_SETTINGS,
      resetFiltersKey: accountAddress
    };
    $[0] = accountAddress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters
  } = useTableSettings(t0);
  let t1;
  if ($[2] !== accountAddress) {
    t1 = {
      accountAddress
    };
    $[2] = accountAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    borrowPositions,
    facets,
    partial,
    loading,
    error,
    isNotFound
  } = useBorrowPositionsTableData(t1);
  let t2;
  if ($[4] !== accountAddress || $[5] !== borrowPositions) {
    t2 = {
      borrowPositions,
      userAddress: accountAddress
    };
    $[4] = accountAddress;
    $[5] = borrowPositions;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const borrowPositionsRows = useBorrowPositionsTableRows(t2);
  let t3;
  if ($[7] !== borrowPositionsRows || $[8] !== columnFilters) {
    t3 = {
      items: borrowPositionsRows,
      columnFilters
    };
    $[7] = borrowPositionsRows;
    $[8] = columnFilters;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    filters,
    filteredItems
  } = useDashboardBorrowPositionsFilters(t3);
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      collateralAssetIdsFilter: "collateralAssetIds",
      loanAssetIdsFilter: "loanAssetIds",
      chainIdsFilter: "chainIds"
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== setColumnFilters) {
    t5 = {
      setColumnFilters,
      filterMapping: t4
    };
    $[11] = setColumnFilters;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    handleFiltersChange
  } = useTableFiltersHandler(t5);
  let t6;
  if ($[13] !== facets?.chainIds) {
    t6 = facets?.chainIds.map(_temp);
    $[13] = facets?.chainIds;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const positionChainIds = t6;
  let t7;
  if ($[15] !== columnFilters || $[16] !== sorting) {
    t7 = {
      sorting,
      columnFilters
    };
    $[15] = columnFilters;
    $[16] = sorting;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== filteredItems || $[19] !== setSorting || $[20] !== t7) {
    t8 = {
      features: borrowPositionsTableFeatures,
      data: filteredItems,
      columns: columnsBorrowPositions,
      onSortingChange: setSorting,
      enableSortingRemoval: false,
      sortDescFirst: true,
      state: t7,
      getRowId: _temp2
    };
    $[18] = filteredItems;
    $[19] = setSorting;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const table = useTable(t8);
  let t9;
  if ($[22] !== borrowPositions || $[23] !== borrowPositionsRows || $[24] !== error || $[25] !== facets || $[26] !== filters || $[27] !== handleFiltersChange || $[28] !== isNotFound || $[29] !== loading || $[30] !== partial || $[31] !== positionChainIds || $[32] !== table) {
    t9 = {
      borrowPositions,
      borrowPositionsRows,
      facets,
      partial,
      loading,
      error,
      isNotFound,
      table,
      filters,
      handleFiltersChange,
      positionChainIds,
      columnLabels: columnLabelsBorrowPositions
    };
    $[22] = borrowPositions;
    $[23] = borrowPositionsRows;
    $[24] = error;
    $[25] = facets;
    $[26] = filters;
    $[27] = handleFiltersChange;
    $[28] = isNotFound;
    $[29] = loading;
    $[30] = partial;
    $[31] = positionChainIds;
    $[32] = table;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
function _temp(facet) {
  return facet.value as ChainId;
}
function _temp2(row) {
  return row.id;
}